import React from 'react';
import { cn } from '@/lib/utils';
import Disclosure from './Disclosure';

function answerText(item) {
  if (typeof item.a === 'string') return item.a;
  if (typeof item.aText === 'string') return item.aText;
  return null;
}

/**
 * schema.org FAQPage JSON-LD for `items`, as a string — the same output the old
 * components/public FaqList gave (site.components.test.jsx pins it), so a page that moved
 * to this list kept its structured data. Answers that are React nodes
 * need a plain-text `aText`; items without a text answer are left out. Null when
 * nothing qualifies.
 */
export function buildFaqJsonLd(items = []) {
  const mainEntity = (items || [])
    .filter((it) => it && typeof it.q === 'string' && answerText(it))
    .map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: answerText(it) },
    }));
  if (!mainEntity.length) return null;
  return JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity });
}

/**
 * Questions and answers as independent disclosures (several can be open, like the
 * reference's `<details>` list). Same `items` shape as the old components/public FaqList.
 * The section heading is the page's (put a <Heading> beside or above the list).
 *
 * Props:
 * - `items`: [{ q, a, aText? }] — `a` a string (wrapped in a <p>) or a node; `aText` =
 *   plain text for a node answer (JSON-LD)
 * - `jsonLd`: true = also render the FAQPage <script type="application/ld+json">
 *   ("<" escaped so an answer cannot close the script)
 * - `id`: list id and item id prefix (default 'faq') → `<id>-<n>-button` / `-panel`
 * - `headingLevel`: optional 2–6, wraps each question button in a heading
 * - `emptyText`: shown (muted) when `items` is empty; default renders nothing
 * - `className` (list), `itemClassName` (each row)
 */
export default function FaqList({ items = [], jsonLd = false, id = 'faq', headingLevel, emptyText, className, itemClassName }) {
  const list = (items || []).filter((it) => it && it.q);
  if (!list.length) {
    return emptyText ? <p className="text-brand-muted">{emptyText}</p> : null;
  }
  const ld = jsonLd ? buildFaqJsonLd(list) : null;
  return (
    <div id={id} className={cn('w-full', className)}>
      {list.map((it, i) => (
        <Disclosure
          // eslint-disable-next-line react/no-array-index-key
          key={`${i}-${typeof it.q === 'string' ? it.q : ''}`}
          id={`${id}-${i}`}
          summary={it.q}
          headingLevel={headingLevel}
          className={itemClassName}
        >
          {typeof it.a === 'string' ? <p className="max-w-[66ch]">{it.a}</p> : it.a}
        </Disclosure>
      ))}
      {ld ? (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: ld.replace(/</g, '\\u003c') }}
        />
      ) : null}
    </div>
  );
}
